/* eslint-disable react/prop-types */
import React from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";

function isChunkLoadError(error) {
  if (!error) return false;
  const msg = (error.message || error.toString() || "").toLowerCase();
  const name = (error.name || "").toLowerCase();
  return (
    name === "chunkyloaderror" ||
    msg.includes("loading chunk") ||
    msg.includes("chunkloaderror") ||
    msg.includes("loading css chunk") ||
    msg.includes("failed to fetch dynamically imported module") ||
    // HTML-as-JS: stale SW returned 404 HTML page
    (msg.includes("unexpected token") && msg.includes("<")) ||
    msg.includes("syntaxerror") ||
    msg.includes("cannot find module")
  );
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      retryKey: 0,
      isChunkError: false,
    };
    this.handleRetry = this.handleRetry.bind(this);
    this.toggleDetails = this.toggleDetails.bind(this);
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);

    if (isChunkLoadError(error)) {
      const alreadyReloaded = sessionStorage.getItem("chunk_reload");
      if (!alreadyReloaded) {
        sessionStorage.setItem("chunk_reload", "1");
        Promise.all([
          "serviceWorker" in navigator
            ? navigator.serviceWorker
                .getRegistrations()
                .then((regs) => Promise.all(regs.map((r) => r.unregister())))
            : Promise.resolve(),
          "caches" in window
            ? caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
            : Promise.resolve(),
        ])
          .catch(() => {})
          .finally(() => window.location.reload());
      } else {
        sessionStorage.removeItem("chunk_reload");
      }
    }
  }

  handleRetry() {
    this.setState((prevState) => ({
      hasError: false,
      error: null,
      showDetails: false,
      isChunkError: false,
      retryKey: prevState.retryKey + 1,
    }));
  }

  // toggle to show error details
  toggleDetails() {
    this.setState((s) => ({ showDetails: !s.showDetails }));
  }

  render() {
    if (!this.state.hasError) {
      return <React.Fragment key={this.state.retryKey}>{this.props.children}</React.Fragment>;
    }

    if (this.state.isChunkError) {
      return (
        <Box
          role="status"
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            bgcolor: "#f5f7fa",
          }}
        >
          <Typography variant="h6" sx={{ color: "text.secondary", fontWeight: 500 }}>
            Updating app, please wait…
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            A new version of the app was deployed. Reloading automatically.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => window.location.reload()}
            sx={{ mt: 1, textTransform: "none" }}
          >
            Reload now
          </Button>
        </Box>
      );
    }

    const { error, showDetails } = this.state;

    return (
      <Box
        role="alert"
        sx={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          bgcolor: "background.paper",
          background: "linear-gradient(135deg, #f5f7fa 0%, #f0f4f8 100%)",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 600,
            textAlign: "center",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            p: 4,
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              position: "absolute",
              top: 16,
              right: 16,
            }}
          >
            <IconButton
              aria-label="close"
              size="small"
              onClick={() => window.location.reload()}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <WarningAmberRoundedIcon
            sx={{
              fontSize: 64,
              color: "error.main",
              mb: 2,
              opacity: 0.8,
            }}
          />

          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: "text.primary",
              mb: 2,
            }}
          >
            Something went wrong
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              mb: 3,
              lineHeight: 1.6,
            }}
          >
            We encountered an unexpected error while loading this page. You can try again or reload
            the application.
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={this.handleRetry}
              startIcon={<RefreshRoundedIcon />}
              sx={{
                px: 4,
                py: 1,
                color: "white",
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
                boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3)",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(25, 118, 210, 0.4)",
                },
              }}
            >
              Try again
            </Button>

            <Button
              variant="outlined"
              onClick={() => window.location.reload()}
              sx={{
                px: 4,
                py: 1,
                color: "text.secondary",
                borderColor: "grey.400",
                fontWeight: 500,
                borderRadius: 2,
                textTransform: "none",
                "&:hover": {
                  borderColor: "grey.600",
                  bgcolor: "action.hover",
                },
              }}
            >
              Reload page
            </Button>
          </Box>

          <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
            <Button
              variant="text"
              onClick={this.toggleDetails}
              sx={{
                color: "text.secondary",
                fontSize: "0.875rem",
                textTransform: "none",
                "&:hover": {
                  bgcolor: "transparent",
                  color: "primary.main",
                },
              }}
            >
              {showDetails ? "Hide technical details" : "Show technical details"}
            </Button>
          </Box>

          <Collapse in={!!showDetails} timeout="auto">
            <Box
              sx={{
                textAlign: "left",
                mt: 2,
                bgcolor: "grey.50",
                p: 3,
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Error Details
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  color: "error.dark",
                }}
              >
                {error?.toString()}
              </Typography>
              {error?.stack && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 2,
                    color: "text.secondary",
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {error.stack}
                </Typography>
              )}
            </Box>
          </Collapse>
        </Box>
      </Box>
    );
  }
}
